import React, {useState} from 'react'
import ReactDOM from 'react-dom'
import {Util} from 'yougile-util'
import {Debouncer} from '@common/debouncer'
import {useUpdateForwardedRef} from '@containers/hooks'

export interface DivWithHintProps
  extends Omit<React.HTMLProps<HTMLDivElement>, 'content'> {
  content?: React.ReactNode
  children?: React.ReactNode
  width?: number | string
  hideHint?: boolean
  componentTag?: string
}

const debouncer = new Debouncer(300)
const displayedHintIds = new Set<number>()

interface HintProps {
  content: React.ReactNode
  x: number
  y: number
  width: number | string
  leftDirection: boolean
}

const HintEl = (props: HintProps) => {
  const {content, x, y, leftDirection, width} = props
  const maxHeight = window.innerHeight - y - 24
  const maxHeightUp = y - 24
  const downDir = maxHeight > 300 || maxHeight > maxHeightUp

  const bem = Util.bemFrom('hint')
  return (
    <div className={bem('item')}>
      {content && (
        <div
          style={{top: y + 'px', left: x + 'px', width}}
          className={bem('wnd', {leftDirection, up: !downDir, down: downDir})}
        >
          {content}
        </div>
      )}
    </div>
  )
}

export class Hint {
  private static hostElement: HTMLElement
  private static initHostElement(): void {
    if (!Hint.hostElement) {
      const el = document.createElement('div')
      Hint.hostElement = el
      document.body.appendChild(el)
    }
  }

  private static setVisibility(value: boolean): void {
    Hint.initHostElement()
    Hint.hostElement.style.display = value ? 'block' : 'none'
  }

  public static show =
    (content: React.ReactNode, hintId?: number, width: number | string = 180) =>
    (e: {target: EventTarget}) => {
      displayedHintIds.clear()
      if (hintId) {
        displayedHintIds.add(hintId)
      }

      const {top, left, height} = (
        e.target as HTMLElement
      ).getBoundingClientRect()

      debouncer.runLatest(() => {
        Hint.setVisibility(true)
        const clientWidth = document.documentElement.clientWidth
        const leftDirection = clientWidth - left < 180
        ReactDOM.render(
          <HintEl
            content={content}
            width={width}
            x={left}
            y={top + height}
            leftDirection={leftDirection}
          />,
          Hint.hostElement
        )
      })
    }

  public static hide(): void
  public static hide(hintId: number): void
  public static hide(hintId?: number): void {
    if (hintId) {
      if (!displayedHintIds.has(hintId)) {
        return
      }
      displayedHintIds.delete(hintId)
    }

    Hint.setVisibility(false)
    debouncer.runLatest(() => Hint.setVisibility(false))
  }
}

/**
 * @deprecated use Tooltip instead
 * import {Tooltip} from '@components/common/tooltip/tooltip'
 */
export const DivWithHint = React.forwardRef<HTMLDivElement, DivWithHintProps>(
  (props, forwardedRef) => {
    const [unmountDebouncer] = useState(new Debouncer(200))
    const [hintId] = useState(() => Math.random())
    const ref = React.useRef(null)
    const updateRef = useUpdateForwardedRef(ref, forwardedRef)

    const [hovered, setHovered] = React.useState(false)
    React.useEffect(
      () => () => unmountDebouncer.runButNotOften(() => Hint.hide(hintId)),
      [unmountDebouncer]
    )

    if (
      hovered &&
      !(ref.current as unknown as HTMLElement)?.matches(':hover')
    ) {
      setHovered(false)
      unmountDebouncer.runButNotOften(() => Hint.hide(hintId))
    }

    const {componentTag, children, content, className, hideHint, ...domProps} =
      props
    const bem = Util.bemFrom('hint')
    const Component: any = componentTag || 'div'

    return hideHint || !content ? (
      <Component
        {...domProps}
        ref={updateRef}
        className={bem('cnt') + ' ' + (className || '')}
      >
        {children}
      </Component>
    ) : (
      <Component
        {...domProps}
        ref={updateRef}
        className={bem('cnt') + ' ' + (className || '')}
        onMouseEnter={(e: Event) => {
          setHovered(true)
          Hint.show(content, hintId, props.width ?? 180)(e)
        }}
        onMouseLeave={() => {
          setHovered(false)
          Hint.hide(hintId)
        }}
      >
        {children}
      </Component>
    )
  }
)
